/**
 * Simple event emitter for daily bonus streak broken notifications.
 *
 * This provides a clean communication channel between processDailyBonusLogin
 * (called from AuthContext) and DailyBonusContext, replacing the fragile
 * sessionStorage-based approach with timing delays.
 *
 * The emitter buffers the most recent event so that late subscribers (due to
 * React useEffect timing) can still receive the notification.
 *
 * Usage:
 * - processDailyBonusLogin calls dailyBonusEvents.emitStreakBroken()
 * - DailyBonusContext subscribes via dailyBonusEvents.onStreakBroken()
 */

export interface StreakBrokenPayload {
  streakBroken: boolean;
  previousStreak: number;
}

type StreakBrokenListener = (payload: StreakBrokenPayload) => void;

class DailyBonusEvents {
  private listeners: Set<StreakBrokenListener> = new Set();
  private pendingEvent: StreakBrokenPayload | null = null;

  /**
   * Subscribe to streak broken events.
   * If an event was emitted before subscription (race condition),
   * the listener will immediately receive the buffered event.
   * @returns Unsubscribe function
   */
  onStreakBroken(listener: StreakBrokenListener): () => void {
    this.listeners.add(listener);

    // Deliver buffered event to late subscriber (fixes race condition)
    if (this.pendingEvent) {
      try {
        listener(this.pendingEvent);
      } catch (error) {
        console.error('[DailyBonusEvents] Error in listener (buffered):', error);
      }
      this.pendingEvent = null;
    }

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Emit a streak broken event to all listeners.
   * If no listeners are registered, buffers the event for late subscribers.
   */
  emitStreakBroken(payload: StreakBrokenPayload): void {
    if (this.listeners.size === 0) {
      // No listeners yet - buffer for late subscriber
      this.pendingEvent = payload;
      return;
    }

    this.listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error('[DailyBonusEvents] Error in listener:', error);
      }
    });
  }

  /**
   * Clear the buffered event (e.g., on logout)
   */
  clearPending(): void {
    this.pendingEvent = null;
  }
}

// Singleton instance
export const dailyBonusEvents = new DailyBonusEvents();
